import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiType, FiMoon, FiSun, FiLink, FiMinusCircle, FiPlusCircle, FiRefreshCw, FiX, FiEye } from 'react-icons/fi';
import { FaWheelchair } from 'react-icons/fa';
import './AccessibilityWidget.css';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    highContrast: false,
    grayscale: false,
    highlightLinks: false,
    readingGuide: false,
    stopAnimations: false,
    darkMode: false
  });

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  // Apply settings to document - apply to #root so widget stays visible
  const applySettings = useCallback((newSettings) => {
    const htmlRoot = document.documentElement;
    const appRoot = document.getElementById('root');

    // Font size
    htmlRoot.style.fontSize = `${newSettings.fontSize}%`;

    if (!appRoot) return;

    // High contrast
    if (newSettings.highContrast) {
      appRoot.classList.add('accessibility-high-contrast');
    } else {
      appRoot.classList.remove('accessibility-high-contrast');
    }

    // Grayscale
    if (newSettings.grayscale) {
      appRoot.classList.add('accessibility-grayscale');
    } else {
      appRoot.classList.remove('accessibility-grayscale');
    }

    // Highlight links
    if (newSettings.highlightLinks) {
      appRoot.classList.add('accessibility-highlight-links');
    } else {
      appRoot.classList.remove('accessibility-highlight-links');
    }

    // Reading guide
    if (newSettings.readingGuide) {
      appRoot.classList.add('accessibility-reading-guide');
    } else {
      appRoot.classList.remove('accessibility-reading-guide');
    }

    // Stop animations
    if (newSettings.stopAnimations) {
      appRoot.classList.add('accessibility-stop-animations');
    } else {
      appRoot.classList.remove('accessibility-stop-animations');
    }

    // Dark mode
    if (newSettings.darkMode) {
      appRoot.classList.add('accessibility-dark-mode');
    } else {
      appRoot.classList.remove('accessibility-dark-mode');
    }

    // Save to localStorage
    localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
  }, []);

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applySettings(newSettings);
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSetting('fontSize', settings.fontSize + 10);
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSetting('fontSize', settings.fontSize - 10);
    }
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      grayscale: false,
      highlightLinks: false,
      readingGuide: false,
      stopAnimations: false,
      darkMode: false
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
  };

  const toggleSetting = (key) => {
    updateSetting(key, !settings[key]);
  };

  // Reading guide mouse follower
  useEffect(() => {
    if (!settings.readingGuide) return;

    const guide = document.createElement('div');
    guide.className = 'reading-guide-line';
    document.body.appendChild(guide);

    const moveGuide = (e) => {
      guide.style.top = `${e.clientY}px`;
    };

    document.addEventListener('mousemove', moveGuide);

    return () => {
      document.removeEventListener('mousemove', moveGuide);
      guide.remove();
    };
  }, [settings.readingGuide]);

  // Render using portal to body so it's outside #root and not affected by filters
  return ReactDOM.createPortal(
    <div className="accessibility-widget">
      <button
        className="accessibility-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="תפריט נגישות"
        title="נגישות"
      >
        <FaWheelchair size={24} />
      </button>

      {isOpen && (
        <div className="accessibility-panel">
          <div className="accessibility-header">
            <h3>נגישות</h3>
            <button onClick={() => setIsOpen(false)} aria-label="סגור">
              <FiX size={20} />
            </button>
          </div>

          <div className="accessibility-options">
            {/* Font Size */}
            <div className="accessibility-option font-size-option">
              <span className="option-label">
                <FiType size={18} />
                גודל טקסט
              </span>
              <div className="font-size-controls">
                <button onClick={decreaseFontSize} aria-label="הקטן טקסט">
                  <FiMinusCircle size={20} />
                </button>
                <span className="font-size-value">{settings.fontSize}%</span>
                <button onClick={increaseFontSize} aria-label="הגדל טקסט">
                  <FiPlusCircle size={20} />
                </button>
              </div>
            </div>

            {/* High Contrast */}
            <button
              className={`accessibility-option ${settings.highContrast ? 'active' : ''}`}
              onClick={() => toggleSetting('highContrast')}
            >
              <FiSun size={18} />
              ניגודיות גבוהה
            </button>

            {/* Grayscale */}
            <button
              className={`accessibility-option ${settings.grayscale ? 'active' : ''}`}
              onClick={() => toggleSetting('grayscale')}
            >
              <FiMoon size={18} />
              גווני אפור
            </button>

            {/* Highlight Links */}
            <button
              className={`accessibility-option ${settings.highlightLinks ? 'active' : ''}`}
              onClick={() => toggleSetting('highlightLinks')}
            >
              <FiLink size={18} />
              הדגשת קישורים
            </button>

            {/* Reading Guide */}
            <button
              className={`accessibility-option ${settings.readingGuide ? 'active' : ''}`}
              onClick={() => toggleSetting('readingGuide')}
            >
              <FiEye size={18} />
              סרגל קריאה
            </button>

            {/* Stop Animations */}
            <button
              className={`accessibility-option ${settings.stopAnimations ? 'active' : ''}`}
              onClick={() => toggleSetting('stopAnimations')}
            >
              <FiMinusCircle size={18} />
              עצירת אנימציות
            </button>

            {/* Reset */}
            <button className="accessibility-option reset-option" onClick={resetSettings}>
              <FiRefreshCw size={18} />
              איפוס הגדרות
            </button>
          </div>

          <div className="accessibility-footer">
            <span>Weon - נגישות לכולם</span>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default AccessibilityWidget;
